import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState
} from 'react';
import { updateApiKey } from 'src/api/capacitor-adapter';
import { SplashScreen } from 'src/components/splash-screen';
import { useNotificationsContext } from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { useMountEffect } from 'src/hooks/use-mount-effect';
import { AuthContextType, Session, getSessions } from './auth-context';

const API_TOKEN_KEY = 'deinverband:authToken';

export interface ApiKeyCallbacks {
  signInWithToken: (token: string) => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
  updatePassword: undefined; // (current: string, password: string) => Promise<void>;
  // resetPassword: (email: string) => Promise<void>;
  // resetPasswordWithEmailLink: (
  //   oobCode: string,
  //   password: string
  // ) => Promise<void>;
  // validateOobCode: (oobCode: string) => Promise<string>;
  // development tools
  sessions: Session[];
}

/**
 * Provides user authentication state
 */
export interface ApiKeyAuthState {
  // login state
  initialized: boolean;
  authenticated: boolean;
  failed: boolean;
  // API key for authentication
  apiKey?: string;
}

const initialState: ApiKeyAuthState = {
  initialized: false,
  authenticated: false,
  failed: false
};

export type ApiKeyContextType = { type: 'api-key' } & AuthContextType &
  ApiKeyCallbacks &
  ApiKeyAuthState;

export const ApiKeyContext = createContext<ApiKeyContextType | undefined>(
  undefined
);

const handleApiKeyAuthChanged = (token: string | null, error: unknown) => {
  console.info('[api-key] Authentication changed:', !!token);

  // TODO: errors are currently not handled properly by the keycloak lib
  // (therefore failed must not be set to true, which would block the UI)
  if (error !== undefined)
    return {
      initialized: true,
      authenticated: false,
      failed: false
    };

  return {
    initialized: true,
    authenticated: !!token,
    failed: false,
    apiKey: token || undefined
  };
};

/**
 * Provide the API key based logic to children
 *
 * NOTE: This component blocks rendering until the API key context has been
 * initialized properly. Children can rely on the context being ready.
 */
export const ApiKeyProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const [state, setState] = useState<ApiKeyAuthState>(initialState);
  const { handleRequestPermissions } = useNotificationsContext();

  const handleState = useCallback(
    async (state: ApiKeyAuthState) => {
      // inject the user into the API (no re-render)
      updateApiKey(state.apiKey || null);
      // update the local state (and cause re-render after API is ready)
      setState(state);
      // provide the token to the service worker
      // await messageServiceWorker('OIDC_TOKEN', state.oidcUser?.access_token);
      // request notifications when authentication changes
      if (!!state.apiKey) handleRequestPermissions('Auth changed');
    },
    // must remain stable to avoid re-rendering the context
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // initialize API key
  useMountEffect(() => {
    const token = localStorage.getItem(API_TOKEN_KEY);
    handleState(handleApiKeyAuthChanged(token, undefined));
  });

  const signInWithToken = useCallback(
    async (token: string): Promise<void> => {
      localStorage.setItem(API_TOKEN_KEY, token);
      handleState(handleApiKeyAuthChanged(token, undefined));
    },
    [handleState]
  );

  const signOut = useCallback(async (): Promise<void> => {
    localStorage.removeItem(API_TOKEN_KEY);
    handleState(handleApiKeyAuthChanged(null, undefined));
  }, [handleState]);

  // sessions are always read from local storage
  // (feature flags are not available)
  const sessions = useMemo(() => getSessions(), []);

  const context = useHookMemo({
    type: 'api-key' as const,
    // auth state
    initialized: state.initialized,
    authenticated: state.authenticated,
    failed: state.failed,
    apiKey: state.apiKey,
    // API key callbacks
    signInWithToken,
    signOut,
    // not supported in this context
    updatePassword: undefined,
    // development tools
    sessions
  });

  // children rely on valid context
  if (!state.initialized || state.failed)
    return (
      <SplashScreen
        layer="api-key"
        error={state.failed}
      />
    );

  return (
    <ApiKeyContext.Provider value={context}>{children}</ApiKeyContext.Provider>
  );
};
